// Представление «Архив переписки»: четыре года Вотсапа заказчика с поиском.
//
// ## Зачем оно в разделе «Суточные расходы»
//
// В архиве авансы, зарплаты и номера карт: 29 228 сообщений из 96 494 содержат карту или
// телефон. Открывают его из денежного вопроса — «сколько я выдал Позднякову за два года», «на
// какую карту переводили в марте», — и стоять он должен там, где этот вопрос возникает.
// Восьмого раздела ради одного экрана в продукте не заводится (ПРАВИЛА.md, раздел 1.1).
//
// ## Почему это поиск, а не лента
//
// Историю нельзя было влить в чаты: автора не к кому привязать (сорок семь записей телефонной
// книги, ноль совпадений с картотекой из 314 человек), записать сообщение прошлой датой нечем,
// а на телефоне всё старше тридцати суток стирается уборщиком. Но главное не в этом: лента
// отвечает на «что происходит», а спрашивают у архива «что было сказано про Позднякова в
// марте». Второе решается поиском, а прокруткой девяноста шести тысяч сообщений — никогда.
//
// ## У каждой находки стоит адрес в источнике
//
// «Авансы.txt:66235» — файл выгрузки и номер строки. Половина этой переписки старше года, и
// заказчик, увидев «выдали 5 000», пойдёт сверять: без адреса ему нечем, он может только
// поверить нам на слово. Это же отличает архив от нашего пересказа его данных.
//
// ## Листаем смещением, а не курсором
//
// В журнале действий листание идёт номером записи, потому что журнал дописывается во время
// чтения и смещение сдвигало бы строки под руками. Здесь наоборот: архив неподвижен по
// устройству — он наполняется переносом выгрузки, а не работой людей, — и смещение безопасно.

import { useState } from 'react';

import { RiChat3Line, RiInformationLine, RiSearchLine } from '@remixicon/react';

import type { ApiError } from '@/shared/errors/api-error';
import type { SearchState } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { formatDateOnly } from '@/shared/time/format';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import * as LinkButton from '@/shared/ui/link-button';
import { DateField } from '@/shared/ui/date-field';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { SearchFilter } from '@/shared/ui/search-filter';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { Toolbar } from '@/shared/ui/toolbar';
import { cn } from '@/utils/cn';

import { useArchiveAuthors, useArchiveChats, useArchiveSearch } from '../queries';
import { FILTER_ARCHIVE_AUTHOR, FILTER_ARCHIVE_CHAT, FILTER_FROM, FILTER_TO } from '../route';

/** Сколько сообщений за раз. Пятьдесят — примерно два экрана прокрутки. */
const СТРАНИЦА = 50;

/** Дальше этого числа строк находка сворачивается: шесть — это высота обычной карточки. */
const СТРОК_БЕЗ_ОБРЕЗКИ = 6;

/** «1 сообщение», «37 204 сообщения». «37204 сообщений» читается как небрежность. */
const СООБЩЕНИЯ = ['сообщение', 'сообщения', 'сообщений'] as const;

export function ChatArchive({
  search,
  onFilter,
  onQuery,
}: {
  search: SearchState;
  onFilter: (key: string, value: string | null) => void;
  onQuery: (value: string) => void;
}) {
  const чат = search.filters[FILTER_ARCHIVE_CHAT];
  const автор = search.filters[FILTER_ARCHIVE_AUTHOR];
  const с = search.filters[FILTER_FROM] ?? '';
  const по = search.filters[FILTER_TO] ?? '';

  const отбор = {
    query: search.q,
    chat: чат,
    author: автор,
    date_from: с === '' ? undefined : с,
    date_to: по === '' ? undefined : по,
  };
  // Страницы накапливаются: «Показать ещё» дописывает список, а не заменяет его. Сколько
  // дочитано, в адрес не пишется намеренно — ссылку присылают на НАЙДЕННОЕ («посмотри, что он
  // писал про Позднякова»), а не на «я долистал до третьей страницы».
  const найденное = useArchiveSearch({ ...отбор, limit: СТРАНИЦА });
  const чаты = useArchiveChats();
  // Авторов спрашиваем по выбранному чату: в «Форме» пишут трое, и предлагать там все сорок
  // семь имён значит прятать нужное имя среди сорока четырёх посторонних.
  const авторы = useArchiveAuthors(чат);

  // Выбранный автор остаётся в списке, даже когда в этом чате он не писал ни разу.
  //
  // Иначе отбор ЕСТЬ, а в поле стоит «все»: человек сменил чат, находки пропали, и причину
  // увидеть негде. Тот же приём, что в полосе вкладок раздела: открытое остаётся в списке, даже
  // если само по себе туда бы не попало.
  const имена = авторы.data?.items ?? [];
  const списокАвторов = автор !== undefined && !имена.includes(автор) ? [автор, ...имена] : имена;

  function сменить(ключ: string, значение: string | null) {
    onFilter(ключ, значение);
  }

  const страницы = найденное.data?.pages ?? [];
  const строки = страницы.flatMap((страница) => страница.items);
  // Сводку сочиняет сервер, и берётся она у ПОСЛЕДНЕЙ пришедшей страницы: там счёт уже
  // накопительный («нашлось 406 сообщений, показано 100»), и пересобирать его здесь значило бы
  // завести второе место, считающее одно и то же число.

  return (
    <main className='flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-bg-white-0'>
      <Toolbar>
        <SearchFilter
          label='Поиск по архиву переписки: фамилия, сумма, слово'
          placeholder='Фамилия, сумма, слово'
          value={search.q ?? ''}
          onApply={onQuery}
        />
        <div className='min-w-56'>
          <SelectField
            id='archive-chat'
            label='Чат'
            value={чат ?? NO_VALUE}
            options={(чаты.data?.items ?? []).map((строка) => ({
              value: строка.source_chat,
              // Число сообщений прямо в названии: оно отвечает на «а есть ли там вообще что
              // искать» раньше, чем человек выберет чат и увидит пустой список.
              label: `${строка.source_chat} · ${countLabel(строка.messages, СООБЩЕНИЯ)}`,
            }))}
            noneLabel='все чаты'
            placeholder='все чаты'
            pending={чаты.isPending}
            failed={чаты.isError}
            failedError={чаты.error as ApiError | null}
            onChange={(значение) =>
              сменить(FILTER_ARCHIVE_CHAT, значение === NO_VALUE ? null : значение)
            }
          />
        </div>
        <div className='min-w-56'>
          <SelectField
            id='archive-author'
            label='Кто писал'
            value={автор ?? NO_VALUE}
            options={списокАвторов.map((имя) => ({ value: имя, label: имя }))}
            noneLabel='все'
            placeholder='все'
            pending={авторы.isPending}
            failed={авторы.isError}
            failedError={авторы.error as ApiError | null}
            onChange={(значение) =>
              сменить(FILTER_ARCHIVE_AUTHOR, значение === NO_VALUE ? null : значение)
            }
          />
        </div>
        <div className='w-40'>
          <DateField
            id='archive-from'
            label='С даты'
            value={с}
            onChange={(значение) => сменить(FILTER_FROM, значение === '' ? null : значение)}
          />
        </div>
        <div className='w-40'>
          <DateField
            id='archive-to'
            label='По дату'
            value={по}
            onChange={(значение) => сменить(FILTER_TO, значение === '' ? null : значение)}
          />
        </div>
      </Toolbar>

      {/* Одна строка о том, чем архив НЕ является.

          Гендиректор заказчика 29.09.2026 ждал, что всё, что пишут в группах («авансы, хоз.
          нужды, все наши группы»), «улетает» в суточные расходы само. Так не будет намеренно:
          обычное сообщение деньгами не становится, иначе любое «дал пять тысяч» превращалось
          бы в проводку. Сумму подтверждают нажатием «Верно?», а сообщение становится
          операцией кнопкой «Сделать операцией» в приложении. Об этом и сказано здесь, на месте,
          где человек ищет свою переписку и задаётся этим вопросом.

          Про вкладки сказано точно: выдача попадает в «Кому выдано», когда её провели, а расход
          в «На что потрачено» только когда финотдел его ПРИНЯЛ (поданная заявка там не считается).
          Стоит под шапкой, а не над ней: вкладки раздела обязаны оставаться на одном месте при
          переключении, иначе они «прыгают». Новой подсказки первого раза не заводим: потолок
          подсказок исчерпан, а строка, которая видна всегда, не сгорает от одного показа.

          До 06.10.2026 строка обещала, что «Сделать операцией» в переписке даёт выдачу в «Кому
          выдано». Неправда: так заводится ПЕРЕДАЧА из остатка одного человека другому, а «Кому
          выдано» — только выдачи из кассы. Теперь названы те пути, которые туда и ведут. */}
      <div className='shrink-0 px-5 pt-3 узко:px-4'>
        <Alert.Root variant='lighter' status='information' size='xsmall' role='note'>
          <Alert.Icon as={RiInformationLine} />
          <span>
            Это архив: только чтение, сообщение само деньгами не становится. Трату отмечают в
            приложении кнопкой «Отчитаться», после приёмки она видна во вкладке «На что потрачено».
            Выдачу оформляют во вкладке «Подотчёт», она видна во вкладке «Кому выдано».
          </span>
        </Alert.Root>
      </div>

      <ScreenState
        data={найденное.data}
        error={найденное.error as ApiError | null}
        subject='архив переписки'
        isEmpty={() => строки.length === 0}
        onRetry={() => void найденное.refetch()}
        loading={
          <div className='flex flex-col gap-2 p-5'>
            <Skeleton className='h-14 w-full' />
            <Skeleton className='h-14 w-full' />
            <Skeleton className='h-14 w-full' />
          </div>
        }
        empty={
          <div className='p-5'>
            <EmptyState
              icon={пустойАрхив(чаты.data?.total) ? RiChat3Line : RiSearchLine}
              illustration='заметки'
              {...пустота(чаты.data?.total)}
            />
          </div>
        }
      >
        {() => (
          <div className='flex min-h-0 flex-1 flex-col'>
            <div className='flex items-baseline justify-between gap-4 px-5 pb-2 pt-3'>
              <span className='text-paragraph-sm text-text-sub-600'>
                {страницы[страницы.length - 1]?.summary ?? ''}
              </span>
            </div>

            <ul className='min-h-0 flex-1 divide-y divide-stroke-soft-200 overflow-y-auto border-t border-stroke-soft-200'>
              {строки.map((сообщение) => (
                <li key={сообщение.id} className='flex items-baseline gap-4 px-5 py-3'>
                  {/* Дата и время разными строками, как в журнале действий: слитная запись
                      завела бы третью грамматику чисел в продукте, где их две. */}
                  <span className='flex w-24 shrink-0 flex-col tabular-nums text-paragraph-xs text-text-sub-600'>
                    <span>{formatDateOnly(сообщение.sent_on)}</span>
                    <span>{сообщение.sent_at ?? ''}</span>
                  </span>
                  <span className='w-40 shrink-0 truncate text-label-sm text-text-strong-950'>
                    {сообщение.author}
                  </span>
                  <div className='flex min-w-0 flex-1 flex-col items-start gap-0.5'>
                    <Тело текст={сообщение.body} />
                    <span className='text-paragraph-xs text-text-sub-600'>
                      {подпись(сообщение.source_chat, сообщение.source_ref)}
                      {сообщение.edited && ' · правили после отправки, сумму стоит сверить'}
                    </span>
                  </div>
                </li>
              ))}
            </ul>

            {найденное.hasNextPage && (
              <div className='shrink-0 border-t border-stroke-soft-200 p-3 text-center'>
                <Button.Root
                  variant='neutral'
                  mode='stroke'
                  size='small'
                  disabled={найденное.isFetchingNextPage}
                  onClick={() => void найденное.fetchNextPage()}
                >
                  {найденное.isFetchingNextPage ? 'Читаем архив…' : 'Показать ещё'}
                </Button.Root>
              </div>
            )}
          </div>
        )}
      </ScreenState>
    </main>
  );
}

/**
 * Откуда строка: чат и номер строки в файле выгрузки.
 *
 * «Авансы · Авансы.txt:91004» и «Зарплата Барк · Чат WhatsApp с контактом Зарплата Барк.txt:150»
 * пишут одно и то же дважды в одной короткой строке — правило про «одно и то же не пишется
 * дважды» тут нарушается на живых данных, а не в теории. Имя файла выгрузки кончается именем
 * чата, файл у чата один, и «чат плюс номер строки» — такой же точный адрес, только короче.
 */
export function подпись(чат: string, адрес: string): string {
  const место = адрес.lastIndexOf(':');
  const файл = место === -1 ? адрес : адрес.slice(0, место);
  const строка = место === -1 ? '' : адрес.slice(место + 1);
  const основа = файл.replace(/\.[^.]+$/, '');
  // Имя файла выгрузки кончается именем чата почти всегда: «Авансы.txt» и «Чат WhatsApp с
  // контактом Зарплата Барк.txt». Повторять его рядом с названием чата незачем — номер строки
  // вместе с чатом и есть точный адрес, а файл у чата один.
  return основа.endsWith(чат) && строка !== '' ? `${чат} · строка ${строка}` : `${чат} · ${адрес}`;
}

/**
 * Текст находки.
 *
 * ## Почему длинное свёрнуто
 *
 * Замер на боевых данных: по слову «Поздняков» нашлось 406 сообщений, и второе из них — список
 * зарплаты на двадцать строк. Развёрнутое, оно занимает экран целиком: человек видит ОДНУ
 * находку из четырёхсот и не может сравнить её с соседней. Находка в поиске — это указатель, а
 * читают её вторым движением.
 *
 * Свёрнуто ровно длинное. Короткие сообщения (половина этой переписки короче девятнадцати
 * знаков) не получают ни обрезки, ни лишней кнопки под собой.
 */
function Тело({ текст }: { текст: string }) {
  const [целиком, setЦеликом] = useState(false);
  const длинное = текст.split('\n').length > СТРОК_БЕЗ_ОБРЕЗКИ;
  return (
    <>
      {/* Переносы строк сохраняются: в чате «Форма» одна выдача занимает пять строк — фамилия
          и позиции, — и склеенная в одну она нечитаема. */}
      <span
        className={cn(
          'whitespace-pre-wrap break-words text-paragraph-sm text-text-strong-950',
          длинное && !целиком && 'line-clamp-6',
        )}
      >
        {текст}
      </span>
      {длинное && (
        <LinkButton.Root
          variant='primary'
          size='small'
          underline
          onClick={() => setЦеликом((было) => !было)}
        >
          {целиком ? 'Свернуть' : 'Показать целиком'}
        </LinkButton.Root>
      )}
    </>
  );
}

/** Архив не наполняли вовсе: чатов в описи ноль. */
function пустойАрхив(чатов: number | undefined): boolean {
  return чатов === 0;
}

/**
 * Два разных пустых экрана, и путать их нельзя (правило `ui-views`).
 *
 * «Перенос ещё не делали» и «за этим отбором ничего нет» требуют РАЗНЫХ действий: в первом
 * случае человеку идти некуда и сказать об этом обязаны мы, во втором — снять отбор.
 */
export function пустота(чатов: number | undefined): { title: string; description: string } {
  if (пустойАрхив(чатов)) {
    return {
      title: 'Архив переписки пока пуст',
      description:
        'Сюда загружается выгрузка чатов Вотсапа. Когда её загрузят, здесь можно будет найти любое сообщение по человеку, дате и словам.',
    };
  }
  return {
    title: 'За этим отбором сообщений нет',
    description:
      'Архив только на чтение, и пусто здесь означает ровно одно: таких сообщений в выгрузке не нашлось. Снимите отбор или попробуйте другое слово.',
  };
}
